import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  Skeleton,
  StatTile,
  StatusPill,
  TableWrap,
  Td,
  Th,
  Tr
} from '../../components/admin/ui';
import {
  IconAlert,
  IconCases,
  IconCheck,
  IconClock,
  IconRefresh,
  IconUser
} from '../../components/icons';
import {
  billedAsLabels,
  billedAsTone,
  caseStatusLabels,
  caseStatusTone,
  formatDate,
  formatNumber,
  inferBilledAs,
  issueTypeLabels
} from '../../components/admin/labels';
import { billingLabel, pilotPreviouslyUsed } from '../../components/plans';
import { UpgradeModal } from '../../components/merchant/PlanUpgradeGrid';

const MerchantDashboard = () => {
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUpgrade, setShowUpgrade] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [me, cases] = await Promise.all([
        api.get('/api/portal/me'),
        api.get('/api/portal/cases?limit=5')
      ]);
      setData(me.data);
      setRecent(cases.data.cases || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load your dashboard.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((k) => (
            <div key={k} className="rounded-xl border border-ink-200 bg-white p-5 shadow-card">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="mt-4 h-8 w-16" />
            </div>
          ))}
        </div>
        <Card>
          <Skeleton className="m-5 h-32" />
        </Card>
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <EmptyState
          icon={IconAlert}
          title="Dashboard unavailable"
          description={error}
          action={
            <Button variant="secondary" size="sm" onClick={fetchAll}>
              <IconRefresh size={15} />
              Try again
            </Button>
          }
        />
      </Card>
    );
  }

  const { merchant, usage, stats } = data;
  const seats = merchant.seats;
  // Already paying: changing plan belongs on the Account page, not as a
  // standing prompt on the dashboard — there is one paid plan, so there is
  // nothing to change to anyway. Still offered to anyone NOT on a live paid
  // plan (a pilot, pay-per-case, or a subscription that has since ended),
  // where choosing one is a real next step.
  const onLivePlan =
    merchant.billingModel === 'subscription' && merchant.subscription?.status !== 'canceled';
  // Each billing model's usage object carries the count under its own field
  // name (pilot: used/allowance, subscription: casesThisCycle/includedCases,
  // pay-per-case: payPerCaseCount with no allowance at all) — the same
  // distinction the case page already branches on for the same reason.
  // freeCaseAllowance is pilot-only history and must not resurface as an
  // "allowance" once billingModel has moved past 'free'.
  const casesAllowed =
    usage?.type === 'pilot' ? usage.allowance ?? merchant.freeCaseAllowance ?? null
      : usage?.type === 'subscription' ? usage.includedCases ?? null
      : null;
  const casesUsed =
    usage?.type === 'pilot' ? usage.used ?? 0
      : usage?.type === 'subscription' ? usage.casesThisCycle ?? 0
      : usage?.type === 'pay_per_case' ? usage.payPerCaseCount ?? 0
      : 0;

  return (
    <div className="space-y-6">
      {/* Trial banner */}
      {usage?.type === 'pilot' && usage.ended ? (
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl bg-warning-50 px-4 py-3.5 ring-1 ring-inset ring-warning-100">
          <div className="flex items-start gap-3">
            <IconAlert size={18} className="mt-0.5 shrink-0 text-warning-600" />
            <p className="text-sm text-warning-700">
              <span className="font-600">Your pilot has ended</span> — upgrade to keep adding
              cases and taking action on the ones you have.
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={() => setShowUpgrade(true)}>
            Upgrade
          </Button>
        </div>
      ) : usage?.type === 'pilot' ? (
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl bg-info-50 px-4 py-3.5 ring-1 ring-inset ring-info-100">
          <div className="flex items-start gap-3">
            <IconClock size={18} className="mt-0.5 shrink-0 text-info-600" />
            <p className="text-sm text-info-700">
              <span className="font-600">Pilot in progress</span> — {usage.used ?? 0} of{' '}
              {usage.allowance ?? merchant.freeCaseAllowance ?? '—'} free cases used.
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={() => setShowUpgrade(true)}>
            See plans
          </Button>
        </div>
      ) : (
        pilotPreviouslyUsed(merchant) && (
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl bg-ink-50 px-4 py-3.5 ring-1 ring-inset ring-ink-100">
            <div className="flex items-start gap-3">
              <IconCheck size={18} className="mt-0.5 shrink-0 text-ink-500" />
              <p className="text-sm text-ink-700">
                Pilot used — you're now on <span className="font-600">{billingLabel(merchant)}</span>.
              </p>
            </div>
            {!onLivePlan && (
              <Button variant="secondary" size="sm" onClick={() => setShowUpgrade(true)}>
                Change plan
              </Button>
            )}
          </div>
        )
      )}

      {/* Stats */}
      <section>
        <div className="mb-3 flex items-center justify-between gap-4">
          <h2 className="font-display text-sm font-700 uppercase tracking-[0.08em] text-ink-500">
            At a glance
          </h2>
          <Button variant="secondary" size="sm" onClick={fetchAll}>
            <IconRefresh size={15} />
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatTile
            label="Awaiting your decision"
            value={formatNumber(stats.openCases)}
            hint="Cases with no action taken yet"
            icon={IconClock}
            tone={stats.openCases > 0 ? 'warning' : 'neutral'}
          />
          <StatTile
            label="Decisions made"
            value={formatNumber(stats.respondedCases)}
            hint="Refunded, reshipped, escalated or rejected"
            icon={IconCheck}
            tone="success"
          />
          <StatTile
            label="Total cases"
            value={formatNumber(stats.totalCases)}
            hint="All time"
            icon={IconCases}
          />
          <StatTile
            label="Seats used"
            value={`${seats.used} / ${seats.allowed}`}
            hint={`${billingLabel(merchant)} plan`}
            icon={IconUser}
            tone={seats.allowed > 0 && seats.used >= seats.allowed ? 'warning' : 'neutral'}
          />
        </div>
      </section>

      {/* Usage this cycle */}
      {usage?.type === 'pay_per_case' ? (
        // No allowance to show a fraction against — pay-per-case cases are
        // paid for individually, one at a time, with no cap.
        <Card>
          <CardHeader title="Cases paid for" description="Billed one case at a time." />
          <div className="px-5 pb-5">
            <p className="text-sm tabular-nums text-ink-900">
              <span className="font-600">{formatNumber(casesUsed)}</span>
              <span className="text-ink-500"> case{casesUsed === 1 ? '' : 's'} paid for</span>
            </p>
          </div>
        </Card>
      ) : (
        casesAllowed != null && (
          <Card>
            <CardHeader
              title="Case allowance"
              description={
                usage?.type === 'subscription' && usage.cycleEnd
                  ? `Resets on ${formatDate(usage.cycleEnd)}.`
                  : 'Cases counted against your plan.'
              }
            />
            <div className="px-5 pb-5">
              <p className="text-sm tabular-nums text-ink-900">
                <span className="font-600">{formatNumber(casesUsed)}</span>
                <span className="text-ink-500"> of {formatNumber(casesAllowed)} used</span>
              </p>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-ink-100">
                <div
                  className={`h-full rounded-[4px] ${
                    casesUsed > casesAllowed ? 'bg-warning-600' : 'bg-brand-600'
                  }`}
                  style={{
                    width: `${
                      casesAllowed > 0 ? Math.min(100, Math.round((casesUsed / casesAllowed) * 100)) : 0
                    }%`
                  }}
                />
              </div>
            </div>
          </Card>
        )
      )}

      {/* Recent cases */}
      <Card>
        <CardHeader
          title="Recent cases"
          description="The five most recent delivery issues raised against your store."
          actions={
            <Button as={Link} to="/merchant/cases" variant="secondary" size="sm">
              View all cases
            </Button>
          }
        />

        {recent.length === 0 ? (
          <EmptyState
            icon={IconCases}
            title="No cases yet"
            description="When a customer reports a delivery issue for your store, it appears here."
          />
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <Th>Case</Th>
                <Th>Customer</Th>
                <Th>Issue</Th>
                <Th>Billed as</Th>
                <Th>Status</Th>
                <Th>Submitted</Th>
              </tr>
            </thead>
            <tbody>
              {recent.map((c) => (
                <Tr key={c._id}>
                  <Td>
                    <Link
                      to={`/merchant/cases/${encodeURIComponent(c.caseId)}`}
                      className="font-600 text-brand-700 hover:underline"
                    >
                      {c.caseId}
                    </Link>
                  </Td>
                  <Td>
                    <div className="font-medium text-ink-900">{c.customer?.name}</div>
                    <div className="text-xs text-ink-500">{c.customer?.email}</div>
                  </Td>
                  <Td className="max-w-[14rem]">
                    <span className="block truncate">
                      {issueTypeLabels[c.issueType] || c.issueType}
                    </span>
                  </Td>
                  <Td>
                    {(() => {
                      const billedAs = inferBilledAs(c);
                      if (billedAs) {
                        return (
                          <StatusPill tone={billedAsTone[billedAs] || 'neutral'}>
                            {billedAsLabels[billedAs] || billedAs}
                          </StatusPill>
                        );
                      }
                      return c.billingCountedAt ? (
                        <span className="text-ink-500">Billed (plan unknown)</span>
                      ) : (
                        <span className="text-ink-500">Not yet</span>
                      );
                    })()}
                  </Td>
                  <Td>
                    <StatusPill tone={caseStatusTone[c.status] || 'neutral'}>
                      {caseStatusLabels[c.status] || c.status}
                    </StatusPill>
                  </Td>
                  <Td className="whitespace-nowrap text-ink-500">{formatDate(c.submittedAt)}</Td>
                </Tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>

      <UpgradeModal open={showUpgrade} onClose={() => setShowUpgrade(false)} merchant={merchant} />
    </div>
  );
};

export default MerchantDashboard;
